import * as THREE from 'three';
import ObjectNameMap from 'components/customer/RoomPlanner/constants/ObjectNameMap';
import {
    NewBasePosition,
    CabinetMesh,
    RoomType,
} from 'components/customer/RoomPlanner/types';

const getOverlapCondition = (child: THREE.Mesh) => {
    return (
        child instanceof THREE.Mesh &&
        !child.userData.ignoreOverlap &&
        child.name != ObjectNameMap.adjustableLeg
    );
};

const checkIfOverlapsOtherCabinets = (
    currentCabinet: THREE.Group<THREE.Object3DEventMap>,
    cabinetList: THREE.Group<THREE.Object3DEventMap>[],
    onOverlap: (value: boolean, product?: CabinetMesh) => void,
    newBasePosition?: NewBasePosition,
    roomType?: RoomType
) => {
    let hasOverlap = false;
    const currentMeshes: THREE.Mesh[] = [];

    currentCabinet.traverse((child) => {
        if (getOverlapCondition(child as THREE.Mesh)) {
            currentMeshes.push(child as THREE.Mesh);
        }
    });

    cabinetList.forEach((otherProduct) => {
        if (
            otherProduct === currentCabinet ||
            otherProduct.uuid === currentCabinet.uuid ||
            (otherProduct.userData.id == currentCabinet.userData.id &&
                Boolean(otherProduct.userData.isPlaceholder) ===
                    Boolean(currentCabinet.userData.isPlaceholder))
        ) {
            return;
        }

        // Get all meshes from both cabinets
        const otherMeshes: THREE.Mesh[] = [];

        otherProduct.traverse((child) => {
            if (getOverlapCondition(child as THREE.Mesh)) {
                otherMeshes.push(child as THREE.Mesh);
            }
        });

        // Check overlap between each mesh pair
        for (const currentMesh of currentMeshes) {
            const currentBox = new THREE.Box3().setFromObject(currentMesh);

            for (const otherMesh of otherMeshes) {
                const otherBox = new THREE.Box3().setFromObject(otherMesh);

                if (currentBox.intersectsBox(otherBox)) {
                    const overlapThreshold = 6.5; // Reduced threshold for more precise detection

                    // Calculate actual overlap distances
                    const xOverlap =
                        Math.min(currentBox.max.x, otherBox.max.x) -
                        Math.max(currentBox.min.x, otherBox.min.x);
                    const zOverlap =
                        Math.min(currentBox.max.z, otherBox.max.z) -
                        Math.max(currentBox.min.z, otherBox.min.z);

                    // Check if the overlap exceeds the threshold
                    if (
                        xOverlap > overlapThreshold &&
                        zOverlap > overlapThreshold
                    ) {
                        hasOverlap = true;
                        break;
                    }
                }
            }
            if (hasOverlap) break;
        }
    });

    let isOutside = false;
    if (
        newBasePosition &&
        (roomType === 'RECTANGULAR' || roomType === 'LSHAPED')
    ) {
        isOutside =
            currentCabinet.position.x < newBasePosition.minX ||
            currentCabinet.position.x > newBasePosition.maxX + 20 ||
            currentCabinet.position.z < newBasePosition.minZ ||
            currentCabinet.position.z > newBasePosition.maxZ + 20;
    }

    onOverlap(hasOverlap || isOutside);
};

const checkIfOverlapsWall = (
    scene: THREE.Scene,
    currentCabinet: THREE.Group<THREE.Object3DEventMap>,
    onOverlap: (value: boolean) => void
) => {
    const wall = scene.children.find(
        (ch) =>
            ch.name === ObjectNameMap.rectangularWall ||
            ch.name === ObjectNameMap.lShapeWall
    ) as THREE.Mesh<THREE.ExtrudeGeometry, THREE.MeshStandardMaterial>;

    wall.children.forEach((wall) => {
        const box = new THREE.Box3().setFromObject(wall);
        const otherBox = new THREE.Box3().setFromObject(currentCabinet);

        if (box.intersectsBox(otherBox)) {
            const overlapThreshold = 22; // increase for more tolerance
            const boxMin = box.min.clone();
            const boxMax = box.max.clone();
            const otherBoxMin = otherBox.min.clone();
            const otherBoxMax = otherBox.max.clone();

            const xOverlap =
                boxMax.x > otherBoxMin.x + overlapThreshold &&
                boxMin.x < otherBoxMax.x - overlapThreshold;
            const zOverlap =
                boxMax.z > otherBoxMin.z + overlapThreshold &&
                boxMin.z < otherBoxMax.z - overlapThreshold;

            if (xOverlap && zOverlap) {
                onOverlap(true);
            } else {
                onOverlap(false);
            }
        }
    });
};

const checkCabinetVerticalPosition = (
    scene: THREE.Scene,

    currentCabinet: THREE.Group,

    onOverlap: (value: boolean) => void
) => {
    // Get floor from scene

    const floor = scene.children.find(
        (ch) => ch.name === ObjectNameMap.floor
    ) as THREE.Mesh; // Get wall to determine ceiling height

    const wall = scene.children.find(
        (ch) =>
            ch.name === ObjectNameMap.rectangularWall ||
            ch.name === ObjectNameMap.lShapeWall
    ) as THREE.Mesh;

    if (!floor || !wall) {
        onOverlap(false);

        return;
    } // Get cabinet bounding box

    const cabinetBox = new THREE.Box3().setFromObject(currentCabinet); // Floor collision check

    const floorBox = new THREE.Box3().setFromObject(floor);

    const floorTopY = floorBox.max.y; // Calculate the actual offset for floor position

    const expectedFloorOffset = 25.72; // From your existing code

    const toleranceRange = 18; // Tolerance for variation

    const actualFloorOffset = floorTopY - cabinetBox.min.y; // Find maximum wall height for ceiling

    let maxWallHeight = 0;

    wall.children.forEach((wallPart) => {
        const partBox = new THREE.Box3().setFromObject(wallPart);

        maxWallHeight = Math.max(maxWallHeight, partBox.max.y);
    }); // Calculate ceiling proximity

    const ceilingY = maxWallHeight;

    const ceilingProximity = ceilingY - 40 - cabinetBox.max.y; // Check both floor and ceiling collision

    const floorCollision =
        actualFloorOffset > expectedFloorOffset + toleranceRange;

    const ceilingCollision = ceilingProximity < toleranceRange;

    if (floorCollision || ceilingCollision) {
        onOverlap(true); // Cabinet overlaps with floor or ceiling
    } else {
        onOverlap(false); // No overlap
    }
};

export const overlapValidator = {
    walls: checkIfOverlapsWall,
    cabinet: checkIfOverlapsOtherCabinets,
    floor: checkCabinetVerticalPosition,
};
